import { driver, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import "~/assets/css/driver-theme.css";
import { authStore } from "~/stores/auth";

type StepPage = "pass-d" | "account";

interface StepDef {
  page: StepPage;
  selector: string;
  titleKey: string;
  descriptionKey: string;
  side: "left" | "right" | "top" | "bottom";
  align: "start" | "center" | "end";
}

const STEPS: StepDef[] = [
  {
    page: "pass-d",
    selector: '[data-e2e="sidebar-user-profile-link"]',
    titleKey: "freelance_onboarding_tour.steps.profile.title",
    descriptionKey: "freelance_onboarding_tour.steps.profile.description",
    side: "right",
    align: "start",
  },
  {
    page: "account",
    selector: '[data-e2e="profile-edit-info-button"]',
    titleKey: "freelance_onboarding_tour.steps.edit_info.title",
    descriptionKey: "freelance_onboarding_tour.steps.edit_info.description",
    side: "bottom",
    align: "end",
  },
  {
    page: "account",
    selector: '[data-e2e="profile-linkedin-import-button"]',
    titleKey: "freelance_onboarding_tour.steps.linkedin_import.title",
    descriptionKey: "freelance_onboarding_tour.steps.linkedin_import.description",
    side: "bottom",
    align: "start",
  },
  {
    page: "pass-d",
    selector: '[data-e2e="sidebar-missions-link"]',
    titleKey: "freelance_onboarding_tour.steps.missions.title",
    descriptionKey: "freelance_onboarding_tour.steps.missions.description",
    side: "right",
    align: "start",
  },
  {
    page: "pass-d",
    selector: '[data-e2e="passd-create-button"]',
    titleKey: "freelance_onboarding_tour.steps.passd.title",
    descriptionKey: "freelance_onboarding_tour.steps.passd.description",
    side: "bottom",
    align: "start",
  },
];

const STEP_KEY = "big:freelance-onboarding-tour-step";
const COMPLETED_KEY = "big:freelance-onboarding-tour-completed";

export const markFreelanceOnboardingTourPending = () => {
  if (import.meta.client) {
    localStorage.setItem(STEP_KEY, "0");
  }
};

const getCurrentStep = (): number | null => {
  const raw = localStorage.getItem(STEP_KEY);
  if (raw === null) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
};

const isCompleted = (): boolean => localStorage.getItem(COMPLETED_KEY) === "1";

const markCompleted = () => {
  localStorage.setItem(COMPLETED_KEY, "1");
  localStorage.removeItem(STEP_KEY);
};

const waitForElement = (selector: string, timeoutMs = 4000): Promise<Element | null> => {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) return resolve(existing);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeoutMs);
  });
};

export const useFreelanceOnboardingTour = (currentPage: StepPage) => {
  const { t } = useI18n();
  const router = useRouter();
  const auth = authStore();

  const shouldStart = (): boolean => {
    if (!import.meta.client) return false;
    if (isCompleted()) return false;
    const step = getCurrentStep();
    if (step === null) return false;
    if (step < 0 || step >= STEPS.length) return false;
    return STEPS[step].page === currentPage;
  };

  const navigateToPage = (page: StepPage) => {
    if (page === "pass-d") {
      router.push("/pass-d");
    } else {
      const slug = auth.user?.profile?.slug;
      if (slug) router.push(`/account/${slug}`);
      else markCompleted(); // safety: no slug, end tour
    }
  };

  const start = async () => {
    if (!import.meta.client) return;

    const startIndex = getCurrentStep();
    if (startIndex === null) return;

    // Pick consecutive steps starting at startIndex that target the current page.
    const localSteps: Array<{ globalIndex: number; def: StepDef }> = [];
    let cursor = startIndex;
    while (cursor < STEPS.length && STEPS[cursor].page === currentPage) {
      localSteps.push({ globalIndex: cursor, def: STEPS[cursor] });
      cursor++;
    }
    if (!localSteps.length) return;

    const lastGlobalIndex = localSteps[localSteps.length - 1].globalIndex;
    const nextGlobalIndex = lastGlobalIndex + 1;

    await Promise.all(localSteps.map((s) => waitForElement(s.def.selector)));

    let willNavigate = false;
    let willComplete = false;
    let tour: Driver;

    tour = driver({
      showProgress: true,
      allowClose: true,
      disableActiveInteraction: true,
      overlayOpacity: 0.6,
      popoverClass: "big-driver-theme",
      nextBtnText: t("freelance_onboarding_tour.next"),
      prevBtnText: t("freelance_onboarding_tour.previous"),
      doneBtnText: t("freelance_onboarding_tour.done"),
      onNextClick: () => {
        const activeIndex = tour.getActiveIndex() ?? 0;
        const isLastLocal = activeIndex === localSteps.length - 1;
        if (!isLastLocal) {
          tour.moveNext();
          return;
        }
        if (nextGlobalIndex >= STEPS.length) {
          willComplete = true;
          markCompleted();
          tour.destroy();
        } else {
          willNavigate = true;
          localStorage.setItem(STEP_KEY, String(nextGlobalIndex));
          tour.destroy();
          navigateToPage(STEPS[nextGlobalIndex].page);
        }
      },
      onCloseClick: () => {
        willComplete = true;
        markCompleted();
        tour.destroy();
      },
      onDestroyed: () => {
        if (!willNavigate && !willComplete) {
          markCompleted();
        }
      },
      steps: localSteps.map((s) => ({
        element: s.def.selector,
        popover: {
          title: t(s.def.titleKey),
          description: t(s.def.descriptionKey),
          side: s.def.side,
          align: s.def.align,
          progressText: `${s.globalIndex + 1} / ${STEPS.length}`,
        },
      })),
    });

    tour.drive();
  };

  return { shouldStart, start };
};
